import React, { Fragment, useEffect, useState } from 'react';
import groupBy from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';
import capitalize from 'lodash/capitalize';
import find from 'lodash/find';
import { useTranslation } from 'react-i18next';
import path from 'path';
import { connect } from 'react-redux';
import Cookies from 'js-cookie';
import { useLastLocation } from 'react-router-last-location';
import { useLocation } from 'react-router-dom';

import { Cell, Grid } from 'formlabs-web-components/Grid';
import { DisplayComponent } from 'formlabs-web-components/BaseComponents';
import { Header } from 'formlabs-web-components/Type';
import { Spinner } from 'formlabs-web-components/Navigation';
import { InteractiveDiv } from 'formlabs-web-components/Wrapper';
import CenterModal from '../../../../common/components/modal/CenterModal';

import { updateLocaleAsync as updateLocaleAction } from '../../../../common/ducks/i18n';
import { migrateCartToStore as migrateCartToStoreAction } from '../../../../common/ducks/cart';
import { getCookieDomain } from '../../../../common/utils';
import RegionFlag from '../../../../common/components/navigation/RegionFlag';

import './CountrySelection.module.css';

const CountrySelection = ({
  countries,
  updateLocale,
  activeRegion,
  migrateCartToStore,
  cartWasMigrated
}) => {
  const location = useLocation();
  const [t] = useTranslation();
  const lastLocation = useLastLocation();
  const [loading, setLoading] = useState(false);
  const [afterMigrationCallback, setAfterMigrationCallback] = useState(null);

  countries = orderBy(countries, ['continent', 'name'], ['desc', 'asc']);
  const continents = groupBy(countries, country => country.continent);

  const pathname = location
    ? location.pathname
    : lastLocation
    ? lastLocation.pathname
    : '';

  const activeCountry = find(countries, { code: activeRegion.toUpperCase() });
  const onStorePage =
    activeCountry.storePage && pathname.indexOf(activeCountry.storePage) !== -1;

  const lastLocationCleaned = pathname
    .replace(/\/[a-z]{0,5}\//g, '')
    .replace(/\/blog\/.*/g, '/blog/')
    .replace(/\/company\/press\/.*/g, '/company/press/')
    .replace(/\/roi\/.*/g, '');

  useEffect(
    () => {
      if (cartWasMigrated && afterMigrationCallback) {
        afterMigrationCallback();
      }
    },
    [cartWasMigrated]
  );

  if (loading) {
    return (
      <Grid direction="y">
        <Cell>
          <Header level={1} displayAs={2} weight={600}>
            {t('Switching Country')}
          </Header>
        </Cell>
        <Cell>
          <Spinner size={8} />
        </Cell>
      </Grid>
    );
  }

  return (
    <div>
      <Header level={1} displayAs={2} weight={600}>
        {t('Choose your Country')}
      </Header>
      {Object.keys(continents).map(continent => (
        <section key={continent} styleName="continent">
          {/* Keep for translation parser:
            t('continent: AS')
            t('continent: EU')
            t('continent: NA')
            t('continent: OC')
            t('continent: SA')
          */}
          <Header key={continent} level={2} displayAs={3} weight={400}>
            {t(`continent: ${continent}`)}
          </Header>
          <Grid
            direction="x"
            gutters="padding"
            addStyleNames="small-up-1 medium-up-3 large-up-5"
          >
            {continents[continent].map(country => {
              const { language, currencyCode, storePage } = country;
              const region = country.code.toLowerCase();
              const to =
                onStorePage && storePage
                  ? storePage
                  : (
                      country.path +
                      (country.path !== '/tr/' &&
                      country.path !== '/cn/' &&
                      country.path !== '/jp/'
                        ? lastLocationCleaned
                        : '')
                    ).replace('//', '/');

              return (
                <Cell
                  key={`country-${country.code}`}
                  backgroundColor={
                    activeCountry.code === country.code ? 'light-blue' : null
                  }
                >
                  <InteractiveDiv
                    onClick={() => {
                      // FIXME workaround until we have a switch cart API
                      const desiredStoreId = `${region}_${language}`;
                      const storeId = Cookies.get('store_view');
                      setLoading(true);

                      if (storeId && desiredStoreId !== storeId) {
                        Cookies.remove('store_view', {
                          domain: getCookieDomain()
                        });
                        Cookies.remove('store_path', {
                          domain: getCookieDomain()
                        });

                        setAfterMigrationCallback(() => () => {
                          updateLocale(language, region, currencyCode);
                          window.location = to;
                        });
                        migrateCartToStore(region);
                      }
                    }}
                  >
                    <div styleName="item">
                      <RegionFlag region={country.code} width={45} />
                      <div styleName="text">
                        <span styleName="country">{country.name}</span>
                        <span>
                          {capitalize(country.languageName.split(',')[0])}
                        </span>
                      </div>
                    </div>
                  </InteractiveDiv>
                </Cell>
              );
            })}
          </Grid>
        </section>
      ))}
    </div>
  );
};

const CountrySelectionComponent = connect(
  state => {
    return {
      activeLanguage: state.i18n.language,
      activeRegion: state.i18n.region,
      cartWasMigrated: state.cart.wasMigrated
    };
  },
  dispatch => {
    return {
      updateLocale: (language, region, currency) => {
        dispatch(updateLocaleAction(language, region, currency));
      },
      migrateCartToStore: storeId => {
        dispatch(migrateCartToStoreAction(storeId));
      }
    };
  }
)(CountrySelection);

const CountrySelectionModal = ({ children, countries }) => {
  const [isShowing, setShowing] = useState(false);

  const show = () => {
    setShowing(true);
  };

  const hide = () => {
    setShowing(false);
  };

  return (
    <Fragment>
      <InteractiveDiv styleName="modal-button" handler={show}>
        {children}
      </InteractiveDiv>
      {isShowing ? (
        <CenterModal width="1200px" onClose={hide}>
          <CountrySelectionComponent countries={countries} hideModal={hide} />
        </CenterModal>
      ) : null}
    </Fragment>
  );
};

export { CountrySelectionModal };

export default DisplayComponent(CountrySelectionComponent);
